import { useEffect, useRef, useState, type FocusEvent as ReactFocusEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { canNavigateToNode, getCareerStage, getStageBoss, isRouteTransitionAllowed, NODE_TYPES, type GameStateV2, type MapNode, type NodeType } from '../game'
import { Hud } from './Hud'
import { announceCompactPanelOpen, listenForOtherCompactPanel } from './compactPanels'

const nodeMeta: Record<NodeType, { label: string; icon: string; description: string }> = {
  match: { label: NODE_TYPES.match.name, icon: NODE_TYPES.match.icon, description: NODE_TYPES.match.description },
  rest: { label: 'Descanso', icon: 'mdi:grass', description: 'Recupera físico o moral.' },
  event: { label: 'Evento', icon: 'mdi:help', description: 'Una decisión con consecuencias.' },
  scout: { label: 'Ojeador', icon: 'mdi:binoculars', description: 'Consigue un consumible o una reliquia para tu build.' },
  derby: { label: 'Derbi', icon: 'mdi:fire', description: 'Ganar suma 3 OVR y mejora físico, moral y prestigio en el club; perder no concede el bonus.' },
}

const worldCupMatchMeta = [
  { column: 0, label: 'Fase de grupos', icon: 'mdi:table-large', description: 'Tres partidos: necesitas ganar al menos dos; con cero o una victoria quedas eliminado.' },
  { column: 2, label: 'Dieciseisavos', icon: 'mdi:tournament', description: 'Primera eliminatoria del cuadro de 32 selecciones.' },
  { column: 3, label: 'Octavos', icon: 'mdi:tournament', description: 'Eliminatoria directa para alcanzar los cuartos de final.' },
] as const

interface SeasonMapProps { state: GameStateV2; onNode: (node: MapNode) => void; onBoss: () => void }

interface NodeHelp {
  id: string
  label: string
  description: string
  icon: string
  x: number
  y: number
  placement: 'above' | 'below'
}

export const routeX = (row: number, nodeCount: number) => nodeCount <= 0 ? 50 : ((row + 0.5) / nodeCount) * 100
export const routeY = (column: number, stepCount: number) => stepCount <= 0 ? 50 : 100 - ((column + 0.5) / stepCount) * 100
const routeCurve = (fromX: number, fromY: number, toX: number, toY: number) => {
  const middleY = (fromY + toY) / 2
  return `M ${fromX} ${fromY} C ${fromX} ${middleY}, ${toX} ${middleY}, ${toX} ${toY}`
}

export function fittedClubNameSize(maxSize: number, availableWidth: number, renderedWidth: number): number {
  if (maxSize <= 0 || availableWidth <= 0 || renderedWidth <= availableWidth) return maxSize
  return Math.max(1, Math.floor(maxSize * availableWidth / renderedWidth))
}

export function SeasonMap({ state, onNode, onBoss }: SeasonMapProps) {
  const legendRef = useRef<HTMLDetailsElement>(null)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const suppressClickIdRef = useRef<string | null>(null)
  const suppressClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const touchStartRef = useRef<{ id: string; x: number; y: number } | null>(null)
  const touchFocusIdRef = useRef<string | null>(null)
  const [nodeHelp, setNodeHelp] = useState<NodeHelp | null>(null)
  const currentColumn = state.week
  const routeShape = state.map.map((column) => column.length)
  const isAvailable = (node: MapNode) => canNavigateToNode(state, node)
  const isChosen = (node: MapNode) => state.path.includes(node.id)
  const nodes = state.map.flat()
  const chosenNodes = state.path
    .map((nodeId) => nodes.find((node) => node.id === nodeId))
    .filter((node): node is MapNode => Boolean(node))
    .sort((left, right) => left.column - right.column)
  const completedPoints = [
    { x: 50, y: 105 },
    ...chosenNodes.map((node) => ({ x: routeX(node.row, state.map[node.column]?.length ?? 1), y: routeY(node.column, state.map.length) })),
  ]
  const currentOrigin = completedPoints.at(-1) ?? { x: 50, y: 105 }
  const currentTargets = state.week < state.map.length
    ? state.map[state.week].filter(isAvailable).map((node) => ({ x: routeX(node.row, state.map[node.column]?.length ?? 1), y: routeY(node.column, state.map.length), id: node.id }))
    : [{ x: 50, y: -10, id: 'boss' }]
  const availableChoiceLabels = state.week < state.map.length
    ? [...new Set(state.map[state.week].filter(isAvailable).map((node) => nodeMeta[node.type].label.toLocaleLowerCase('es')))]
    : []
  const availableChoiceText = availableChoiceLabels.length <= 1
    ? availableChoiceLabels[0] ?? 'una opción'
    : `${availableChoiceLabels.slice(0, -1).join(', ')} o ${availableChoiceLabels.at(-1)}`
  const futurePaths = state.map.slice(0, -1).flatMap((column, columnIndex) => column.flatMap((node) => {
    const nextColumn = state.map[columnIndex + 1] ?? []
    return nextColumn
      .filter((nextNode) => isRouteTransitionAllowed(node.row, nextNode, routeShape))
      .map((nextNode) => ({ id: `${node.id}-${nextNode.id}`, from: node, to: nextNode }))
  }))
  const stage = getCareerStage(state.season, state.finaleKind)
  const stageBoss = getStageBoss(state.season, state.club, state.leagueMovements, state.finaleKind)
  const isWorldCupFinale = state.season === 5 && state.finaleKind === 'world-cup'
  const tooltipId = 'route-node-help'

  const clearHoverTimer = () => {
    if (hoverTimerRef.current !== null) clearTimeout(hoverTimerRef.current)
    hoverTimerRef.current = null
  }

  const clearLongPressTimer = () => {
    if (longPressTimerRef.current !== null) clearTimeout(longPressTimerRef.current)
    longPressTimerRef.current = null
  }

  const showNodeHelp = (id: string, meta: { label: string; icon: string; description: string }, target: HTMLElement) => {
    const rect = target.getBoundingClientRect()
    const tooltipWidth = Math.min(280, Math.max(160, window.innerWidth - 24))
    const halfWidth = tooltipWidth / 2
    const x = Math.min(window.innerWidth - 12 - halfWidth, Math.max(12 + halfWidth, rect.left + rect.width / 2))
    const placement = rect.top >= 170 ? 'above' : 'below'
    setNodeHelp({
      id,
      label: meta.label,
      description: meta.description,
      icon: meta.icon,
      x,
      y: placement === 'above' ? rect.top - 10 : rect.bottom + 10,
      placement,
    })
  }

  const nodeHelpProps = (id: string, meta: { label: string; icon: string; description: string }) => ({
    'data-node-help-target': id,
    'aria-describedby': nodeHelp?.id === id ? tooltipId : undefined,
    onPointerEnter: (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.pointerType && event.pointerType !== 'mouse') return
      clearHoverTimer()
      const target = event.currentTarget
      hoverTimerRef.current = setTimeout(() => showNodeHelp(id, meta, target), 300)
    },
    onPointerLeave: (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.pointerType && event.pointerType !== 'mouse') return
      clearHoverTimer()
      setNodeHelp((current) => current?.id === id ? null : current)
    },
    onPointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.pointerType !== 'touch' && event.pointerType !== 'pen') {
        touchFocusIdRef.current = null
        return
      }
      touchFocusIdRef.current = id
      clearLongPressTimer()
      touchStartRef.current = { id, x: event.clientX, y: event.clientY }
      const target = event.currentTarget
      longPressTimerRef.current = setTimeout(() => {
        suppressClickIdRef.current = id
        showNodeHelp(id, meta, target)
      }, 500)
    },
    onPointerMove: (event: ReactPointerEvent<HTMLButtonElement>) => {
      const start = touchStartRef.current
      if (!start || start.id !== id) return
      if (Math.hypot(event.clientX - start.x, event.clientY - start.y) <= 10) return
      clearLongPressTimer()
      touchStartRef.current = null
    },
    onPointerUp: () => {
      clearLongPressTimer()
      touchStartRef.current = null
      setNodeHelp((current) => current?.id === id ? null : current)
      if (suppressClickIdRef.current !== id) return
      if (suppressClearTimerRef.current !== null) clearTimeout(suppressClearTimerRef.current)
      suppressClearTimerRef.current = setTimeout(() => {
        if (suppressClickIdRef.current === id) suppressClickIdRef.current = null
      }, 700)
    },
    onPointerCancel: () => {
      clearLongPressTimer()
      touchStartRef.current = null
      setNodeHelp((current) => current?.id === id ? null : current)
      if (suppressClickIdRef.current === id) suppressClickIdRef.current = null
    },
    onFocus: (event: ReactFocusEvent<HTMLButtonElement>) => {
      if (touchFocusIdRef.current === id) return
      showNodeHelp(id, meta, event.currentTarget)
    },
    onBlur: () => {
      if (touchFocusIdRef.current === id) touchFocusIdRef.current = null
      setNodeHelp((current) => current?.id === id ? null : current)
    },
  })

  useEffect(() => listenForOtherCompactPanel('legend', () => {
    if (legendRef.current) legendRef.current.open = false
  }), [])

  useEffect(() => {
    clearHoverTimer()
    clearLongPressTimer()
    touchStartRef.current = null
    touchFocusIdRef.current = null
    setNodeHelp(null)
  }, [state.season, state.week])

  useEffect(() => () => {
    clearHoverTimer()
    clearLongPressTimer()
    if (suppressClearTimerRef.current !== null) clearTimeout(suppressClearTimerRef.current)
  }, [])

  useEffect(() => {
    if (!nodeHelp) return
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setNodeHelp(null)
    }
    const closeOutside = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null
      if (target?.closest(`[data-node-help-target="${nodeHelp.id}"]`)) return
      setNodeHelp(null)
    }
    window.addEventListener('keydown', closeOnEscape)
    document.addEventListener('pointerdown', closeOutside)
    return () => {
      window.removeEventListener('keydown', closeOnEscape)
      document.removeEventListener('pointerdown', closeOutside)
    }
  }, [nodeHelp])

  return (
    <section className="route-panel" aria-label={`${stage.name}, etapa ${state.season} de 5`}>
      <Hud state={state} />
      <div className="route-guide" aria-live="polite">
        <div className="route-guide__action">
          <span className="route-guide__step">Ahora</span>
          <Icon icon={state.week < state.map.length ? 'mdi:gesture-tap' : 'mdi:trophy-outline'} />
          <span>{state.week < state.map.length
            ? `Periodo ${state.week + 1}/${state.map.length} · Elige ${availableChoiceText}`
            : `Has llegado al final: juega ${stageBoss.name}`}</span>
        </div>
      </div>
      <div className={`season-map${isWorldCupFinale ? ' season-map--world-cup' : ''}`} aria-label={`Mapa de la etapa ${state.season}`}>
        <details ref={legendRef} className="route-legend" onToggle={(event) => {
          if (event.currentTarget.open) announceCompactPanelOpen('legend')
        }}>
          <summary className="map-toolbar-control"><Icon icon="mdi:map-legend" /><span>Leyenda</span></summary>
          <div className="route-legend__panel">
            <h3>Tipos de nodo</h3>
            <ul className="route-legend__types">
              {Object.values(nodeMeta).map((meta) => <li key={meta.label}><Icon icon={meta.icon} /><span><strong>{meta.label}</strong><small>{meta.description}</small></span></li>)}
              <li><Icon icon="mdi:trophy" /><span><strong>Final de etapa</strong><small>Gana las rondas exigidas.</small></span></li>
              <li className="route-legend__scoring"><Icon icon="mdi:counter" /><span><strong>Progresión</strong><small>8 puntos para avanzar · +1 a +3 OVR por minutos y contribuciones.</small></span></li>
            </ul>
            <h3>Estado del recorrido</h3>
            <ul className="route-legend__states" aria-label="Cómo leer el recorrido">
              <li><i className="is-chosen" /><span><strong>Visitado</strong><small>Tu recorrido completado</small></span></li>
              <li><i className="is-available" /><span><strong>Elige</strong><small>Opciones disponibles</small></span></li>
              <li><i className="is-discarded" /><span><strong>Descartado</strong><small>Alternativas de periodos superados</small></span></li>
              <li><i className="is-future" /><span><strong>Después</strong><small>Posibles caminos</small></span></li>
            </ul>
          </div>
        </details>
        <span className="season-map__direction season-map__direction--finish"><Icon icon="mdi:flag-checkered" /> META</span>
        <span className="season-map__direction season-map__direction--start">INICIO <Icon icon="mdi:arrow-up" /></span>
        <div className="season-map__goal" aria-hidden="true" />
        <svg className="route-network" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <g className="route-network__future">
            {state.map[0]?.filter((node) => isRouteTransitionAllowed(null, node, routeShape)).map((node) => <path key={`start-${node.id}`} d={routeCurve(50, 105, routeX(node.row, state.map[0]?.length ?? 1), routeY(node.column, state.map.length))} />)}
            {futurePaths.map(({ id, from, to }) => <path key={id} d={routeCurve(routeX(from.row, state.map[from.column]?.length ?? 1), routeY(from.column, state.map.length), routeX(to.row, state.map[to.column]?.length ?? 1), routeY(to.column, state.map.length))} />)}
            {state.map.at(-1)?.map((node) => <path key={`boss-${node.id}`} d={routeCurve(routeX(node.row, state.map.at(-1)?.length ?? 1), routeY(node.column, state.map.length), 50, -10)} />)}
          </g>
          <g className="route-network__completed">
            {completedPoints.slice(1).map((point, index) => {
              const previous = completedPoints[index]
              return <path key={`completed-${index}`} d={routeCurve(previous.x, previous.y, point.x, point.y)} />
            })}
          </g>
          <g className="route-network__available">
            {currentTargets.map((target) => <path key={`available-${target.id}`} d={routeCurve(currentOrigin.x, currentOrigin.y, target.x, target.y)} />)}
          </g>
        </svg>
        <button
          className="boss-node"
          type="button"
          aria-disabled={state.week < state.map.length}
          tabIndex={state.week < state.map.length ? -1 : 0}
          aria-label={state.week < state.map.length ? `${stageBoss.name} bloqueado. Faltan ${state.map.length - state.week} periodos.` : `${stageBoss.name}. Debes ganar ${stageBoss.requiredWins} de ${stageBoss.rounds.length}.`}
          {...nodeHelpProps('boss', { label: stageBoss.name, icon: 'mdi:trophy', description: stageBoss.description })}
          onClick={(event) => {
            if (suppressClickIdRef.current === 'boss') {
              suppressClickIdRef.current = null
              event.preventDefault()
              return
            }
            setNodeHelp(null)
            if (state.week >= state.map.length) onBoss()
          }}
        >
          <Icon icon="mdi:trophy" /><strong>{stageBoss.name}</strong><small>{state.week < state.map.length ? `${state.map.length - state.week} periodos pendientes` : `${stageBoss.requiredWins} de ${stageBoss.rounds.length} · ${stageBoss.awardsTitle ? 'título en juego' : 'avanzar'}`}</small>
        </button>
        <div className="route-columns" style={{ gridTemplateRows: `repeat(${state.map.length}, minmax(0, 1fr))` }}>
          {[...state.map].reverse().map((column, reverseIndex) => {
            const columnIndex = state.map.length - reverseIndex - 1
            return <div className="route-column" style={{ gridTemplateColumns: `repeat(${column.length}, minmax(0, 1fr))` }} key={columnIndex}>{column.map((node) => {
              const meta = isWorldCupFinale && node.type === 'match'
                ? worldCupMatchMeta.find((phase) => phase.column === node.column) ?? nodeMeta.match
                : nodeMeta[node.type]
              const status = isChosen(node) ? 'chosen' : isAvailable(node) ? 'available' : node.column < currentColumn ? 'discarded' : node.column === currentColumn ? 'blocked' : 'future'
              const isCurrent = status === 'chosen' && node.id === chosenNodes.at(-1)?.id
              const statusLabel = status === 'available'
                ? 'elige esta opción'
                : isCurrent
                  ? 'posición actual'
                  : status === 'chosen'
                    ? 'completado'
                    : status === 'discarded'
                      ? 'ruta descartada'
                      : 'bloqueado'
              return (
                <button
                  key={node.id}
                  className={`route-node route-node--${node.type} is-${status}${isCurrent ? ' is-current' : ''}`}
                  type="button"
                  aria-disabled={!isAvailable(node)}
                  tabIndex={isAvailable(node) ? 0 : -1}
                  aria-label={`${meta.label}. ${meta.description} Estado: ${statusLabel}.`}
                  {...nodeHelpProps(node.id, meta)}
                  onClick={(event) => {
                    if (suppressClickIdRef.current === node.id) {
                      suppressClickIdRef.current = null
                      event.preventDefault()
                      return
                    }
                    setNodeHelp(null)
                    if (isAvailable(node)) onNode(node)
                  }}
                >
                  <Icon icon={isCurrent ? 'mdi:account' : status === 'chosen' ? 'mdi:check-bold' : status === 'discarded' ? 'mdi:close' : meta.icon} /><strong>{meta.label}</strong><small>{statusLabel}</small>
                </button>
              )
            })}</div>
          })}
        </div>
        <span
          className={`player-marker${chosenNodes.length ? ' player-marker--origin' : ''}`}
          aria-label={chosenNodes.length ? 'Punto de inicio de la ruta' : 'Punto de inicio del jugador'}
        >
          <Icon icon={chosenNodes.length ? 'mdi:flag-variant-outline' : 'mdi:account'} />
        </span>
      </div>
      {nodeHelp && typeof document !== 'undefined' && createPortal(
        <aside
          id={tooltipId}
          className="route-node-tooltip"
          data-placement={nodeHelp.placement}
          role="tooltip"
          style={{ left: `${nodeHelp.x}px`, top: `${nodeHelp.y}px` }}
        >
          <Icon icon={nodeHelp.icon} aria-hidden="true" />
          <span><strong>{nodeHelp.label}</strong><small>{nodeHelp.description}</small></span>
        </aside>,
        document.body,
      )}
    </section>
  )
}
